const SESSION_KEY = 'apc.session';
const TOKEN_KEY = 'apc.accessToken';
const TOKEN_EXPIRES_KEY = 'apc.tokenExpiresAt';
const AUTH_SOURCE_KEY = 'apc.authSource';
const PASSKEY_SETUP_KEY = 'apc.passkeySetup';
const PASSKEY_SUCCESS_KEY = 'apc.passkeySuccess';
const REMEMBER_EMAIL_KEY = 'apc.rememberedEmail';
const REMEMBER_PASSWORD_KEY = 'apc.rememberedPassword';

export type AuthSource = 'backend' | 'privy';

export type AuthUser = {
    id?: string;
    email?: string;
    displayName?: string;
    name?: string;
    status?: string;
    [key: string]: unknown;
};

function readJson<T>(storage: Storage, key: string): T | null {
    try {
        const raw = storage.getItem(key);
        return raw ? (JSON.parse(raw) as T) : null;
    } catch {
        return null;
    }
}

export function parseExpiresIn(expiresIn?: string, from = Date.now()) {
    const match = expiresIn?.trim().match(/^(\d+)([dhms])$/i);

    if (!match) {
        return from + 24 * 60 * 60 * 1000;
    }

    const value = Number(match[1]);
    const unit = match[2].toLowerCase();
    const duration =
        unit === 'd'
            ? value * 24 * 60 * 60 * 1000
            : unit === 'h'
              ? value * 60 * 60 * 1000
              : unit === 'm'
                ? value * 60 * 1000
                : value * 1000;

    return from + duration;
}

export function getTokenExpiresAt() {
    const raw = localStorage.getItem(TOKEN_EXPIRES_KEY) || sessionStorage.getItem(TOKEN_EXPIRES_KEY);
    const expiresAt = raw ? Number(raw) : NaN;
    return Number.isFinite(expiresAt) ? expiresAt : null;
}

export function isSessionValid() {
    const token = localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY);
    const expiresAt = getTokenExpiresAt();

    if (!token || !expiresAt || Date.now() >= expiresAt) {
        if (token || expiresAt) {
            clearAuthTokens(getAuthSource() === 'privy');
        }
        return false;
    }

    return true;
}

export function getSession(): AuthUser | null {
    if (!isSessionValid()) {
        localStorage.removeItem(SESSION_KEY);
        sessionStorage.removeItem(SESSION_KEY);
        return null;
    }

    return (
        readJson<AuthUser>(localStorage, SESSION_KEY) ||
        readJson<AuthUser>(sessionStorage, SESSION_KEY)
    );
}

export function saveSession(user: AuthUser) {
    const payload = JSON.stringify(user);
    localStorage.setItem(SESSION_KEY, payload);
    sessionStorage.removeItem(SESSION_KEY);
}

export function saveRememberedCredentials(email: string, password: string) {
    localStorage.setItem(REMEMBER_EMAIL_KEY, email);
    localStorage.setItem(REMEMBER_PASSWORD_KEY, password);
}

export function clearRememberedCredentials() {
    localStorage.removeItem(REMEMBER_EMAIL_KEY);
    localStorage.removeItem(REMEMBER_PASSWORD_KEY);
}

export function getRememberedCredentials() {
    return {
        email: localStorage.getItem(REMEMBER_EMAIL_KEY) || '',
        password: localStorage.getItem(REMEMBER_PASSWORD_KEY) || '',
    };
}

export function getAccessToken() {
    if (!isSessionValid()) {
        return null;
    }

    return localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY);
}

export function getAuthSource(): AuthSource {
    return localStorage.getItem(AUTH_SOURCE_KEY) === 'privy' ? 'privy' : 'backend';
}

export function hasPasskeySetup() {
    return localStorage.getItem(PASSKEY_SETUP_KEY) === 'true';
}

export function markPasskeySetup() {
    localStorage.setItem(PASSKEY_SETUP_KEY, 'true');
}

function readPasskeySuccess() {
    return readJson<Record<string, string>>(localStorage, PASSKEY_SUCCESS_KEY) ?? {};
}

export function hasPasskeyLoginSuccess(accountKeys: string[]) {
    const saved = readPasskeySuccess();
    return accountKeys.some((key) => Boolean(key) && saved[key] === 'success');
}

export function markPasskeyLoginSuccess(accountKeys: string[]) {
    const saved = readPasskeySuccess();
    let changed = false;
    accountKeys.forEach((key) => {
        if (!key || saved[key] === 'success') return;
        saved[key] = 'success';
        changed = true;
    });
    if (changed) localStorage.setItem(PASSKEY_SUCCESS_KEY, JSON.stringify(saved));
}

export function saveAccessToken(
    token: string,
    expiresAt: number,
    source: AuthSource = 'backend'
) {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(TOKEN_EXPIRES_KEY, String(expiresAt));
    localStorage.setItem(AUTH_SOURCE_KEY, source);
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(TOKEN_EXPIRES_KEY);
    sessionStorage.removeItem(AUTH_SOURCE_KEY);
}

function clearAuthTokens(preserveSource = false) {
    localStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXPIRES_KEY);
    sessionStorage.removeItem(TOKEN_EXPIRES_KEY);
    if (!preserveSource) {
        localStorage.removeItem(AUTH_SOURCE_KEY);
        sessionStorage.removeItem(AUTH_SOURCE_KEY);
    }
}

export function clearSession() {
    localStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(SESSION_KEY);
    clearAuthTokens();
}
